(() => {
  const DESKTOP_QUERY = '(min-width: 990px) and (any-pointer: fine)';
  const COARSE_QUERY = '(any-pointer: coarse)';
  const INTERCOM_LAUNCHERS = [
    '.intercom-lightweight-app-launcher',
    '.intercom-launcher-frame',
    'iframe[name="intercom-launcher-frame"]',
    '#intercom-container .intercom-launcher',
  ];
  const INTERCOM_BOTTOM = 'calc(var(--desktop-sticky-purchase-height, 6.8rem) + var(--nw-cookie-card-stack-height, 0px) + 1.2rem)';

  class DesktopStickyPurchase extends HTMLElement {
    constructor() {
      super();
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.coarseQuery = window.matchMedia(COARSE_QUERY);
      this.panel = this.querySelector('[data-desktop-sticky-panel]');
      this.primary = this.querySelector('[data-desktop-sticky-primary]');
      this.primaryLabel = this.querySelector('[data-desktop-sticky-primary-label]');
      this.variantTitle = this.querySelector('[data-desktop-sticky-variant-title]');
      this.selectionDivider = this.querySelector('[data-desktop-sticky-selection-divider]');
      this.currentPrice = this.querySelector('[data-desktop-sticky-current-price]');
      this.comparePrice = this.querySelector('[data-desktop-sticky-compare-price]');
      this.image = this.querySelector('[data-desktop-sticky-image]');
      this.colorValue = this.querySelector('[data-desktop-sticky-color-value]');
      this.swatches = this.querySelector('[data-desktop-sticky-swatches]');
      this.swatchButtons = Array.from(this.querySelectorAll('[data-desktop-sticky-swatch]'));
      this.variantDataSource = this.querySelector('[data-desktop-sticky-variants]');
      this.variants = this.parseVariantData();
      this.intercomStyles = new Map();
      this.onBreakpointChange = () => this.scheduleSync();
      this.onSectionChange = () => this.scheduleSync();
      this.onOriginMutation = () => this.scheduleSync();
      this.onBodyMutation = () => this.scheduleViewportSync();
      this.onViewportChange = () => this.scheduleViewportSync();
      this.onPanelResize = () => this.measure();
      this.onIntercomMutation = () => this.positionIntercom();
      this.onSwatchClick = (event) => {
        const button = event.target.closest('[data-desktop-sticky-swatch]');
        if (!button || !this.swatches?.contains(button) || button.disabled) return;
        this.selectSwatch(button);
      };
      this.onPrimaryClick = (event) => {
        event.preventDefault();
        const action = this.originalAction;
        if (!action || action.disabled || action.getAttribute('aria-disabled') === 'true') return;
        action.mobileStickyTrigger = this.primary;
        action.click();
      };
    }

    connectedCallback() {
      this.section = this.closest('section');
      this.buyBox = this.closest('.product__buy-buttons')
        || this.section?.querySelector('.product__buy-buttons');
      this.origin = this.buyBox?.querySelector('[data-mobile-sticky-origin]');
      this.form = document.getElementById(this.dataset.formId);
      this.variantSelects = this.section?.querySelector('variant-selects, variant-radios');

      this.desktopQuery.addEventListener('change', this.onBreakpointChange);
      this.coarseQuery.addEventListener('change', this.onBreakpointChange);
      this.section?.addEventListener('change', this.onSectionChange);
      this.primary?.addEventListener('click', this.onPrimaryClick);
      this.swatches?.addEventListener('click', this.onSwatchClick);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      if (this.origin) {
        this.originObserver = new MutationObserver(this.onOriginMutation);
        this.originObserver.observe(this.origin, {
          attributes: true,
          attributeFilter: ['aria-busy', 'aria-disabled', 'disabled', 'hidden', 'style'],
          childList: true,
          subtree: true,
        });
      }

      if (document.body) {
        this.bodyObserver = new MutationObserver(this.onBodyMutation);
        this.bodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      this.cartLockObserver = new MutationObserver((records) => {
        const locked = document.documentElement.classList.contains('nw-cart-drawer-locked');
        if (records.some((record) => (record.oldValue || '').split(/\s+/).includes('nw-cart-drawer-locked') !== locked)) {
          this.scheduleViewportSync();
        }
      });
      this.cartLockObserver.observe(document.documentElement, {
        attributes: true, attributeFilter: ['class'], attributeOldValue: true,
      });

      if ('ResizeObserver' in window && this.panel) {
        this.resizeObserver = new ResizeObserver(this.onPanelResize);
        this.resizeObserver.observe(this.panel);
      }

      this.sync();
    }

    disconnectedCallback() {
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
      this.coarseQuery.removeEventListener('change', this.onBreakpointChange);
      this.section?.removeEventListener('change', this.onSectionChange);
      this.primary?.removeEventListener('click', this.onPrimaryClick);
      this.swatches?.removeEventListener('click', this.onSwatchClick);
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      this.originObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.cartLockObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      if (this.viewportFrame) cancelAnimationFrame(this.viewportFrame);
      this.setActive(false);
    }

    parseVariantData() {
      try {
        return JSON.parse(this.variantDataSource?.textContent || '{}');
      } catch (error) {
        console.error('Desktop sticky purchase variant data is invalid.', error);
        return {};
      }
    }

    get originalAdd() {
      return this.origin?.querySelector('[data-mobile-sticky-original-add]');
    }

    get backInStock() {
      return this.origin?.querySelector('[data-nw-restock-trigger]')
        || this.origin?.querySelector('.klaviyo-bis-trigger');
    }

    get originalAction() {
      const backInStock = this.backInStock;
      const backInStockActive = Boolean(
        backInStock
        && !backInStock.hidden
        && backInStock.style.display !== 'none'
      );
      return backInStockActive ? backInStock : this.originalAdd;
    }

    get selectedVariant() {
      const currentVariantId = this.variantSelects?.currentVariant?.id
        || this.form?.querySelector('input[name="id"]')?.value;
      return this.variants[String(currentVariantId)] || null;
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    scheduleViewportSync() {
      if (this.viewportFrame) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.syncVisibility();
      });
    }

    resolveSwatchControl(button) {
      const targetId = button.dataset.targetInput;
      const targetInput = targetId ? document.getElementById(targetId) : null;
      if (targetInput) return targetInput;

      const optionName = button.dataset.optionName;
      if (!optionName) return null;
      return Array.from(this.section?.querySelectorAll('variant-selects select') || [])
        .find((select) => select.name === `options[${optionName}]`) || null;
    }

    selectSwatch(button) {
      const control = this.resolveSwatchControl(button);
      if (!control) return;

      if (control.matches('input[type="radio"]')) {
        if (control.checked) {
          this.syncSwatches();
          return;
        }
        control.checked = true;
      } else if (control.matches('select')) {
        if (control.value === button.dataset.optionValue) {
          this.syncSwatches();
          return;
        }
        control.value = button.dataset.optionValue;
      } else {
        return;
      }

      control.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncSwatches();
      this.scheduleSync();
    }

    syncSwatches() {
      let selectedValue = '';
      this.swatchButtons.forEach((button) => {
        const control = this.resolveSwatchControl(button);
        const isSelected = Boolean(
          control
          && (
            (control.matches('input[type="radio"]') && control.checked)
            || (control.matches('select') && control.value === button.dataset.optionValue)
          )
        );
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        if (isSelected) selectedValue = button.dataset.optionValue || '';
      });
      if (this.colorValue && selectedValue) this.colorValue.textContent = selectedValue;
    }

    sync() {
      this.syncVariant();
      this.syncSwatches();
      this.syncAction();
      this.syncVisibility();
    }

    syncVariant() {
      const variant = this.selectedVariant;
      if (!variant) return;

      if (this.variantTitle) {
        this.variantTitle.textContent = variant.title || '';
        this.variantTitle.hidden = !variant.title;
      }
      if (this.selectionDivider) this.selectionDivider.hidden = !variant.title;
      if (this.currentPrice) this.currentPrice.textContent = variant.price || '';
      if (this.comparePrice) {
        this.comparePrice.textContent = variant.compareAt || '';
        this.comparePrice.hidden = !variant.compareAt;
      }
      if (this.image && variant.imageUrl && this.image.getAttribute('src') !== variant.imageUrl) {
        this.image.setAttribute('src', variant.imageUrl);
      }
      const colorOptionIndex = Number.parseInt(this.dataset.colorOptionIndex, 10);
      const selectedColor = Number.isInteger(colorOptionIndex) && Array.isArray(variant.options)
        ? variant.options[colorOptionIndex]
        : '';
      if (this.colorValue && selectedColor) this.colorValue.textContent = selectedColor;
    }

    syncAction() {
      const action = this.originalAction;
      const actionDisabled = Boolean(
        !action
        || action.disabled
        || action.getAttribute('aria-disabled') === 'true'
      );
      const isNotify = Boolean(action?.matches?.('[data-nw-restock-trigger], .klaviyo-bis-trigger'));
      const isNativeRestock = Boolean(action?.matches?.('[data-nw-restock-trigger]'));
      const actionLabel = action?.querySelector('[data-product-submit-label]')
        || action?.querySelector('span');
      const label = isNativeRestock
        ? 'NOTIFY ME'
        : actionLabel?.textContent.trim().replace(/\s+/g, ' ');

      if (this.primary) {
        this.primary.disabled = actionDisabled;
        this.primary.classList.toggle('is-notify', isNotify);
        this.primary.dataset.nwCommerceAction = action === this.originalAdd ? 'add' : 'restock';
        this.primary.toggleAttribute('aria-busy', action?.getAttribute('aria-busy') === 'true');
      }
      if (this.primaryLabel) {
        this.primaryLabel.textContent = label || (
          actionDisabled
            ? (this.dataset.soldOutLabel || 'Sold out')
            : (this.dataset.addLabel || 'Add to cart')
        );
      }
    }

    syncVisibility() {
      const action = this.originalAction;
      const overlayOpen = document.documentElement.classList.contains('nw-cart-drawer-locked')
        || document.body?.classList.contains('overflow-hidden')
        || document.body?.classList.contains('overflow-hidden-tablet');
      const desktopEligible = this.desktopQuery.matches && !this.coarseQuery.matches;

      if (!desktopEligible || !action || overlayOpen) {
        this.setActive(false);
        return;
      }

      const inlineRestock = action.matches('[data-nw-restock-trigger]')
        ? this.section?.querySelector('nw-restock-notice:not([hidden])')
        : null;
      const anchor = inlineRestock || action;
      const actionBounds = anchor.getBoundingClientRect();
      const actionHasLayout = actionBounds.width > 0 && actionBounds.height > 0;
      const actionPassedViewport = actionHasLayout && actionBounds.bottom <= 0;
      const footer = document.getElementById('shopify-section-footer');
      const footerBounds = footer?.getBoundingClientRect();
      const footerReached = Boolean(footerBounds && footerBounds.top <= window.innerHeight);

      this.setActive(actionPassedViewport && !footerReached);
    }

    setActive(isActive) {
      const changed = this.classList.contains('is-active') !== isActive;
      this.classList.toggle('is-active', isActive);
      this.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      this.panel?.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      document.documentElement.classList.toggle('desktop-sticky-purchase-active', isActive);

      if (isActive) {
        this.measure();
        this.startIntercomPositioning();
      } else {
        if (changed) {
          document.documentElement.style.removeProperty('--desktop-sticky-purchase-height');
        }
        this.clearIntercomPositioning();
      }
    }

    startIntercomPositioning() {
      if (!this.intercomObserver && document.body) {
        this.intercomObserver = new MutationObserver(this.onIntercomMutation);
        this.intercomObserver.observe(document.body, { childList: true, subtree: true });
      }
      this.positionIntercom();
    }

    positionIntercom() {
      if (!this.classList.contains('is-active')) return;
      const launchers = document.querySelectorAll(INTERCOM_LAUNCHERS.join(','));
      launchers.forEach((launcher) => {
        this.intercomObserver?.observe(launcher, {
          attributes: true,
          attributeFilter: ['style'],
        });
        const value = launcher.style.getPropertyValue('bottom');
        const priority = launcher.style.getPropertyPriority('bottom');
        // Ignore our own mutation; preserve the latest offset supplied by Intercom.
        if (value === INTERCOM_BOTTOM && priority === 'important') return;
        this.intercomStyles.set(launcher, { value, priority });
        launcher.style.setProperty('bottom', INTERCOM_BOTTOM, 'important');
      });
    }

    clearIntercomPositioning() {
      this.intercomObserver?.disconnect();
      this.intercomObserver = null;
      this.intercomStyles.forEach((previous, launcher) => {
        // A native write can arrive before its MutationObserver callback. Only
        // undo our own value, including on launchers temporarily detached by the app.
        if (launcher.style.getPropertyValue('bottom') !== INTERCOM_BOTTOM
          || launcher.style.getPropertyPriority('bottom') !== 'important') return;
        if (previous.value) {
          launcher.style.setProperty('bottom', previous.value, previous.priority);
        } else {
          launcher.style.removeProperty('bottom');
        }
      });
      this.intercomStyles.clear();
    }

    measure() {
      if (!this.classList.contains('is-active') || !this.panel) return;
      const height = Math.ceil(this.panel.getBoundingClientRect().height);
      if (height <= 0) return;
      document.documentElement.style.setProperty('--desktop-sticky-purchase-height', `${height}px`);
    }
  }

  if (!customElements.get('desktop-sticky-purchase')) {
    customElements.define('desktop-sticky-purchase', DesktopStickyPurchase);
  }
})();
